// Server API client. Every response carries when the data was fetched
// upstream and whether it is a stale fallback (see src/cache.rs).

export interface Meta {
  /// Unix seconds when the server fetched this from upstream.
  asof: number;
  /// Upstream is failing; this is the last good copy.
  stale: boolean;
}

export interface Resp<T> {
  data: T;
  meta: Meta;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

export async function get<T>(
  path: string,
  params: Record<string, string | number | undefined> = {},
  signal?: AbortSignal,
): Promise<Resp<T>> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined) qs.set(k, String(v));
  const url = `/api/${path}${qs.size ? "?" + qs : ""}`;
  const r = await fetch(url, { signal });
  if (!r.ok) {
    let msg = `${r.status} ${r.statusText}`;
    try {
      msg = ((await r.json()) as { error?: string }).error ?? msg;
    } catch {
      // not JSON
    }
    throw new ApiError(r.status, msg);
  }
  const data = (await r.json()) as T;
  return {
    data,
    meta: {
      asof: Number(r.headers.get("x-berg-asof") ?? 0),
      stale: r.headers.get("x-berg-stale") === "1",
    },
  };
}

export interface Quote {
  symbol: string;
  shortName?: string;
  longName?: string;
  quoteType?: string;
  typeDisp?: string;
  currency?: string;
  exchange?: string;
  fullExchangeName?: string;
  exchangeTimezoneName?: string;
  exchangeTimezoneShortName?: string;
  marketState?: string;
  regularMarketPrice?: number;
  regularMarketChange?: number;
  regularMarketChangePercent?: number;
  regularMarketTime?: number;
  regularMarketOpen?: number;
  regularMarketDayHigh?: number;
  regularMarketDayLow?: number;
  regularMarketPreviousClose?: number;
  regularMarketVolume?: number;
  bid?: number;
  ask?: number;
  bidSize?: number;
  askSize?: number;
  averageDailyVolume3Month?: number;
  averageDailyVolume10Day?: number;
  fiftyTwoWeekHigh?: number;
  fiftyTwoWeekLow?: number;
  fiftyTwoWeekChangePercent?: number;
  fiftyDayAverage?: number;
  twoHundredDayAverage?: number;
  marketCap?: number;
  sharesOutstanding?: number;
  trailingPE?: number;
  forwardPE?: number;
  priceToBook?: number;
  epsTrailingTwelveMonths?: number;
  epsForward?: number;
  epsCurrentYear?: number;
  dividendYield?: number;
  dividendRate?: number;
  trailingAnnualDividendYield?: number;
  bookValue?: number;
  earningsTimestamp?: number;
  preMarketPrice?: number;
  preMarketChange?: number;
  preMarketChangePercent?: number;
  preMarketTime?: number;
  postMarketPrice?: number;
  postMarketChange?: number;
  postMarketChangePercent?: number;
  postMarketTime?: number;
  averageAnalystRating?: string;
  exchangeDataDelayedBy?: number;
  quoteSourceName?: string;
  priceHint?: number;
  financialCurrency?: string;
  openInterest?: number;
  expireDate?: number;
  underlyingSymbol?: string;
  circulatingSupply?: number;
  volume24Hr?: number;
  ytdReturn?: number;
  netAssets?: number;
  netExpenseRatio?: number;
}

export interface Chart {
  meta: {
    symbol?: string;
    currency?: string;
    exchangeName?: string;
    instrumentType?: string;
    exchangeTimezoneName?: string;
    gmtoffset?: number;
    regularMarketPrice?: number;
    chartPreviousClose?: number;
    previousClose?: number;
    longName?: string;
    shortName?: string;
    dataGranularity?: string;
  };
  t: number[];
  o: (number | null)[];
  h: (number | null)[];
  l: (number | null)[];
  c: number[];
  v: (number | null)[];
  adj: number[] | null;
  dividends: { t: number; amount: number }[];
  splits: { t: number; splitRatio: string }[];
}

export type Spark = Record<string, { t: number[]; c: number[]; prev?: number }>;

export interface SearchHit {
  symbol: string;
  name?: string;
  exchange?: string;
  type?: string;
}

export const quotes = (syms: string[], signal?: AbortSignal) =>
  get<Quote[]>("quote", { s: syms.join(",") }, signal);

export const chart = (s: string, range: string, interval: string, signal?: AbortSignal) =>
  get<Chart>("chart", { s, range, interval }, signal);

/// Sparkline series, chunked to the server's 20-symbol limit.
export async function spark(syms: string[], range = "1d", interval = "5m", signal?: AbortSignal): Promise<Spark> {
  const out: Spark = {};
  const chunks: string[][] = [];
  for (let i = 0; i < syms.length; i += 20) chunks.push(syms.slice(i, i + 20));
  const parts = await Promise.all(
    chunks.map((c) => get<Spark>("spark", { s: c.join(","), range, interval }, signal).catch(() => null)),
  );
  for (const p of parts) if (p) Object.assign(out, p.data);
  return out;
}

export const search = (q: string, signal?: AbortSignal) => get<SearchHit[]>("search", { q }, signal);

/// Yahoo quoteSummary modules, `{raw, fmt}` already flattened to raw values.
/// Loosely typed: each function reads the fields it needs.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Summary = Record<string, any>;
export const summary = (s: string, signal?: AbortSignal) => get<Summary>("summary", { s }, signal);
